import { Moment } from "moment";
import React, {
  KeyboardEventHandler,
  MouseEventHandler,
  useMemo,
  useRef,
  useState
} from "react";
import ReactSelect, {
  ActionMeta,
  components,
  InputActionMeta,
  MultiValue,
  MultiValueGenericProps
} from "react-select";
import Select from "react-select/base";
import { FlowState, QueryItem } from "../@types";
import {
  addLoadMoreOptionsIfNecessary,
  valueIsKeywordOperator
} from "../utils";

import DatePickerModal from "../../../../../components/DatePickerModal";
import { formatDate, isDateField } from "../../../util";
import { FLOW_STATES } from "../consts";
import {
  createNewGroupOption,
  createNewOption,
  createOptionsArrayWithComplexValue
} from "../utils/createNewOptionsUtils";
import {
  createOptionFromLiveString,
  flowStateIsCreatable,
  nextFlowState,
  stringIsCreatable
} from "../utils/flowControlUtils";
import {
  convertSuggestionsToOptions,
  getSuggestionsBasedOnState
} from "../utils/suggestionUtils";
import AutosuggestMenu from "./AutosuggestMenu";

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_PAGE_SIZE_INCREMENT = 50;

type IEditValueContainerProps = MultiValueGenericProps<QueryItem, true> & {
  onFinishEditing: () => void;
};

const EditValueContainer = (props: IEditValueContainerProps) => {
  const allGroups = useMemo(
    () => (props.selectProps.value || []) as readonly QueryItem[],
    [props.selectProps.value]
  );
  const queryData = props.data as QueryItem;
  const previousValue =
    queryData.grouped?.[queryData.grouped.length - 1]?.value;
  const lastFlowState =
    queryData.grouped?.[queryData.grouped.length - 1]?.state;
  const keywordIsDate = !!queryData.keyword && isDateField(queryData.keyword);
  const initialFlowState = lastFlowState
    ? nextFlowState(lastFlowState, previousValue)
    : FLOW_STATES.ALL_KEYWORDS;
  const initialEditValue =
    keywordIsDate && queryData.grouped && queryData.grouped.length > 0
      ? [queryData.grouped[0]]
      : queryData.grouped?.filter(({ value }) => value) ?? [];

  const [flowState, setFlowState] = useState<FlowState>(
    keywordIsDate ? FLOW_STATES.SINGLE_OR_MULTI_VALUE : initialFlowState
  );
  const [editValue, setEditValue] =
    useState<readonly QueryItem[]>(initialEditValue);
  const [editInputValue, setEditInputValue] = useState<string>("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isInteractingWithMenu, setIsInteractingWithMenu] = useState(false);

  const ref = useRef<Select<QueryItem, true>>(null);
  const userTypedKeyword = useRef(false);

  const menuIsOpen =
    flowState !== FLOW_STATES.ALL_KEYWORDS || editValue.length === 0;

  const allSuggestions = useMemo(
    () =>
      flowState === FLOW_STATES.ALL_KEYWORDS && editValue.length !== 0
        ? []
        : getSuggestionsBasedOnState({
            flowState,
            allGroups,
            currentGroup: editValue,
            currentGroupKey: queryData.itemKey
          }),
    [flowState, allGroups, editValue, queryData.itemKey]
  );

  const filteredSuggestions = useMemo(() => {
    const lowerCaseInputValue = editInputValue.toLowerCase();
    return addLoadMoreOptionsIfNecessary(
      convertSuggestionsToOptions(
        editInputValue,
        flowState,
        allSuggestions.filter(suggestion =>
          suggestion.value.toLowerCase().includes(lowerCaseInputValue)
        )
      ),
      pageSize
    );
  }, [editInputValue, flowState, allSuggestions, pageSize]);

  const isValidNewOption = (newOptionInput: string) =>
    flowStateIsCreatable(flowState) &&
    (flowState !== FLOW_STATES.ALL_KEYWORDS || editValue.length === 0) &&
    stringIsCreatable(newOptionInput, flowState, editValue);

  const saveEditValue = (
    newValue: readonly QueryItem[],
    newValuePosition?: "before" | "after",
    newGroupState?: FlowState
  ) => {
    const trimmedInputValue = editInputValue.trim();
    const newEditValue =
      trimmedInputValue && isValidNewOption(trimmedInputValue)
        ? [
            ...newValue,
            createNewOption({
              value: trimmedInputValue,
              flowState
            })
          ]
        : newValue;

    if (newEditValue.length === 0) {
      props.selectProps.onEditValue?.(queryData, null);
      return;
    }

    const newGroupOption = createNewGroupOption({
      value: "",
      flowState: newGroupState ?? queryData.state,
      grouped: newEditValue as QueryItem[],
      isEditing: false,
      keyword: newEditValue[0]?.value
    });
    props.selectProps.onEditValue?.(queryData, newGroupOption);
    if (newValuePosition)
      props.selectProps.onAddEmptyValue?.(newGroupOption, newValuePosition);
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    if (
      showDatePicker ||
      valueIsKeywordOperator(editInputValue) ||
      userTypedKeyword.current
    )
      return;
    props.onFinishEditing();
    saveEditValue(editValue);
  };

  const handleOnChange = (
    newValue: MultiValue<QueryItem>,
    actionMeta: ActionMeta<QueryItem>
  ) => {
    if (actionMeta.option?.value.trim() === "") return;

    if (!isInteractingWithMenu && actionMeta.action === "select-option") return;

    if (
      flowState === FLOW_STATES.ALL_KEYWORDS &&
      editValue.length > 0 &&
      (actionMeta.action === "select-option" ||
        actionMeta.action === "create-option")
    )
      return;

    if (
      actionMeta.option?.value === "__more__" &&
      actionMeta.action === "select-option"
    ) {
      setPageSize(prev =>
        Math.min(prev + DEFAULT_PAGE_SIZE_INCREMENT, allSuggestions.length)
      );
      setIsInteractingWithMenu(true);
      setTimeout(() => {
        ref.current?.setState({
          focusedOption:
            ref.current?.getFocusableOptionsWithIds()?.[
              filteredSuggestions.length - 1
            ].data ?? null
        });
        if (ref.current) ref.current.scrollToFocusedOptionOnUpdate = true;
      }, 0);
      return;
    }

    if (actionMeta.option?.value.endsWith("__datepicker")) {
      setShowDatePicker(true);
      return;
    }

    setIsInteractingWithMenu(false);
    setEditInputValue("");

    if (
      (actionMeta.action === "create-option" ||
        actionMeta.action === "select-option") &&
      actionMeta.option
    ) {
      if (valueIsKeywordOperator(actionMeta.option.value ?? "")) {
        userTypedKeyword.current = true;
        props.onFinishEditing();
        const newKeywordOption = createNewOption({
          value: actionMeta.option.value,
          flowState
        });
        const newValues = [...editValue, newKeywordOption];
        setEditValue(newValues);
        saveEditValue(newValues);
        return;
      }

      const newOptions = createOptionsArrayWithComplexValue({
        currentGroup: editValue as QueryItem[],
        optionToAdd: actionMeta.option,
        flowState
      });
      setEditValue(prevState => [...prevState, ...newOptions]);
      if (actionMeta.option.complexValue) {
        setEditInputValue("");
        setFlowState(
          nextFlowState(
            newOptions[newOptions.length - 1].state,
            newOptions[newOptions.length - 1].value
          )
        );
        return;
      }
    } else {
      setEditValue(newValue);
    }

    if (actionMeta.action === "pop-value") {
      if (!actionMeta.removedValue) {
        setFlowState(FLOW_STATES.ALL_KEYWORDS);
        setEditValue([]);
        return;
      }
      setEditInputValue(actionMeta.removedValue.value);
      setFlowState(actionMeta.removedValue.state);
      return;
    }

    if (actionMeta.action === "clear") {
      setFlowState(FLOW_STATES.ALL_KEYWORDS);
      setEditValue([]);
      return;
    }

    if (
      actionMeta.action === "remove-value" ||
      actionMeta.action === "deselect-option"
    ) {
      setEditValue(newValue);
      if (newValue.length === 0) {
        setFlowState(FLOW_STATES.ALL_KEYWORDS);
      } else {
        const lastGroupElement = newValue[newValue.length - 1];
        setFlowState(
          nextFlowState(lastGroupElement.state, lastGroupElement.value)
        );
      }
      return;
    }

    const newFlowState = nextFlowState(
      flowState,
      newValue[newValue.length - 1]?.value
    );
    setFlowState(newFlowState);
  };

  const handleOnInputChange = (
    newValue: string,
    actionMeta: InputActionMeta
  ) => {
    const newOption = createOptionFromLiveString(newValue, flowState);

    if (
      newOption &&
      !(flowState === FLOW_STATES.ALL_KEYWORDS && editValue.length > 0)
    ) {
      handleOnChange([...editValue, newOption], {
        action: "create-option",
        option: newOption
      });
      setEditInputValue("");
      return;
    }

    if (actionMeta.action !== "set-value") {
      setEditInputValue(newValue);
      setPageSize(DEFAULT_PAGE_SIZE);
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = event => {
    event.stopPropagation();
    if (event.key === "Enter" && !isInteractingWithMenu) {
      event.preventDefault();
      props.onFinishEditing();
      saveEditValue(editValue);
    }

    if (event.key === "Tab" && !isInteractingWithMenu) {
      event.preventDefault();
      if (isValidNewOption(editInputValue)) {
        const newOption = createNewOption({
          value: editInputValue,
          flowState,
          useValueAsItemKey: true
        });
        handleOnChange([...editValue, newOption], {
          action: "create-option",
          option: newOption
        });
        setEditInputValue("");
      }
    }

    if (
      event.key === "Backspace" &&
      event.ctrlKey &&
      !isInteractingWithMenu &&
      !editInputValue
    ) {
      event.preventDefault();
      event.stopPropagation();
      handleOnChange(editValue.slice(0, -1), {
        action: "remove-value",
        removedValue: editValue[editValue.length - 1]
      });
    }

    switch (event.key) {
      case "Escape":
        event.preventDefault();
        if (!queryData?.grouped?.length) {
          props.selectProps.onEditValue?.(queryData, null);
        }
        props.onFinishEditing();
        setEditInputValue("");
        setEditValue(queryData?.grouped ?? []);
        setIsInteractingWithMenu(false);
        break;
      case "ArrowDown":
        if (!menuIsOpen) break;

        if (!isInteractingWithMenu) {
          event.preventDefault();
          ref.current?.focusOption("first");
        }
        setIsInteractingWithMenu(true);
        break;
      case "ArrowUp":
        if (!menuIsOpen) break;

        if (
          isInteractingWithMenu &&
          ref.current?.focusedOptionRef?.id ===
            ref.current?.getFocusableOptionsWithIds()[0]?.id
        ) {
          event.preventDefault();
          ref.current?.focusInput();
          setIsInteractingWithMenu(false);
          break;
        }

        if (!isInteractingWithMenu) {
          event.stopPropagation();
          event.preventDefault();
          ref.current?.setState({
            focusedOption:
              filteredSuggestions[filteredSuggestions.length - 1] ?? null
          });
          if (ref.current) ref.current.scrollToFocusedOptionOnUpdate = true;
        }
        setIsInteractingWithMenu(true);
        break;
      default:
        setIsInteractingWithMenu(false);
        break;
    }
  };

  return (
    <>
      <ReactSelect<QueryItem, true>
        ref={ref}
        autoFocus
        placeholder={""}
        options={filteredSuggestions}
        closeMenuOnSelect={false}
        menuIsOpen={menuIsOpen && filteredSuggestions.length > 0}
        maxMenuHeight={500}
        tabSelectsValue
        isMulti
        classNames={props.selectProps.classNames}
        inputValue={editInputValue}
        getOptionValue={option => option.itemKey}
        value={editValue}
        onInputChange={handleOnInputChange}
        onChange={handleOnChange}
        filterOption={() => true}
        menuPortalTarget={props.selectProps.menuPortalTarget}
        components={{
          DropdownIndicator: null,
          IndicatorSeparator: null,
          Menu: AutosuggestMenu
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        styles={{
          container: base => ({
            ...base,
            marginRight: 20
          }),
          control: base => ({
            ...base,
            width: "100%",
            border: "none",
            borderColor: "transparent",
            boxShadow: "none",
            overflow: "hidden",
            flexWrap: "nowrap"
          }),
          valueContainer: base => ({
            ...base,
            flexWrap: "nowrap",
            overflow: "hidden"
          }),
          menu: props.selectProps.styles.menu,
          input: props.selectProps.styles.input,
          menuList: props.selectProps.styles.menuList,
          menuPortal: props.selectProps.styles.menuPortal,
          multiValueLabel: base => ({
            ...base,
            paddingRight: 6,
            fontSize: "13px"
          }),
          option: (base, optionProps) => ({
            ...base,
            padding: 0,
            margin: 0,
            backgroundColor:
              optionProps.isFocused && isInteractingWithMenu
                ? base.backgroundColor
                : "transparent"
          }),
          multiValueRemove: (base, state) => ({
            ...base,
            display:
              state.data.state === FLOW_STATES.MULTI_VALUE ||
              (state.data.state === FLOW_STATES.MULTI_VALUE_OR_RANGE &&
                state.data.value !== "TO") ||
              (state.data.state === FLOW_STATES.FIRST_MULTI_VALUE &&
                (state.selectProps.value as QueryItem[])[state.index + 1]
                  ?.value !== "TO") ||
              (state.data.state === FLOW_STATES.SINGLE_OR_MULTI_VALUE &&
                state.selectProps.value &&
                (state.selectProps.value as QueryItem[]).length === 2) ||
              (state.data.state === FLOW_STATES.ALL_KEYWORDS &&
                state.selectProps.value &&
                (state.selectProps.value as QueryItem[]).length === 1)
                ? "flex"
                : "none"
          }),
          clearIndicator: base => ({ ...base, display: "none" })
        }}
        onMenuMouseLeave={() => setIsInteractingWithMenu(false)}
        onMenuMouseEnter={() => setIsInteractingWithMenu(true)}
      />
      <DatePickerModal
        show={showDatePicker}
        onClose={setShowDatePicker}
        onApply={({ start, end }: { start: Moment; end: Moment }) => {
          const formatedStart = formatDate(start);
          const formatedEnd = formatDate(end);
          const timeValue = `[${formatedStart} TO ${formatedEnd}]`;
          handleOnChange(editValue, {
            action: "create-option",
            option: {
              value: timeValue,
              label: timeValue,
              state: flowState,
              itemKey: timeValue,
              complexValue: [formatedStart, "TO", formatedEnd, "]"]
            }
          });
          setShowDatePicker(false);
          setIsInteractingWithMenu(false);
          ref.current?.blur();
        }}
      />
    </>
  );
};

const MultiValueContainer = (
  props: MultiValueGenericProps<QueryItem, true>
) => {
  const queryData = props.data as QueryItem;

  const handleClick: MouseEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    props.selectProps?.onOptionClick?.(queryData);
  };

  const handleFinishEditing = () =>
    props.selectProps?.onOptionClick?.(queryData);

  const handleAddEmptyValue = () =>
    props.selectProps?.onAddEmptyValue?.(queryData, "before");

  return queryData.isEditing ? (
    <EditValueContainer {...props} onFinishEditing={handleFinishEditing} />
  ) : (
    <div className={"search-autosuggest__value"}>
      <div
        className={"search-autosuggest__add-value"}
        onClick={handleAddEmptyValue}
      >
        <div className={"search-autosuggest__add-value-icon"}>+</div>
      </div>
      <div onClick={handleClick}>
        <components.MultiValueContainer {...props}>
          {props.children}
        </components.MultiValueContainer>
      </div>
    </div>
  );
};

export default MultiValueContainer;
